import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuill } from 'react-quilljs'
import { useParams } from 'react-router-dom'
import { Form, notification } from 'antd'
import Const from 'constants/constant'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import useSWR, { useSWRConfig } from 'swr'
import { FileMagicNumber } from 'types/building'
import Utils from 'utils'
import Resizer from 'utils/imageResizer'

import useTourUploadImage, { TourUploadImagePath } from '../useTourUploadImage'

export default function useUpdateBusiness({
  handleCancel
}: {
  handleCancel: () => void
}) {
  const { t } = useTranslation()
  const { id: buildingId } = useParams()
  const { directory } = useDirectory()
  const isCommunity = directory === 'community'
  const apiPrefix = isCommunity ? '/v1/tour' : '/v1'
  const [isLoadingInitData, setIsLoadingInitData] = useState(false)
  const { data: buildingInfo, isLoading } = useSWR<Building>({
    url: `${apiPrefix}/buildings/${buildingId}`
  })

  const [form] = Form.useForm()
  // State for business image
  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const [initialBusinessImage, setInitialBusinessImage] = useState<
    string | undefined
  >(undefined)
  const [isSavingData, setIsSavingData] = useState(false)

  const [isLogoLinkHasErrors, setIsLogoLinkHasErrors] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleChooseFile = () => inputRef?.current?.click()
  const initialThumbnail = form.getFieldValue('businessImage')

  const businessLogoImage = selectedFile
    ? URL.createObjectURL(selectedFile)
    : initialThumbnail

  const description = Form.useWatch('description', form)
  const businessImage = Form.useWatch('businessImage', form)
  const isFormValid = description && businessImage && !isLogoLinkHasErrors

  const { trigger: updateBusiness, isMutating: updating } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`
  )

  const { trigger: deleteLogo, isMutating: deleting } = useMutation(
    `${apiPrefix}/buildings/${buildingId}/update-extra-info`,
    {},
    true
  )

  const { uploadFile } = useTourUploadImage({
    buildingId: Number(buildingId),
    path: TourUploadImagePath.BUSINESS,
    selectedFile
  })
  // description
  const { quill, quillRef } = useQuill({
    theme: 'snow',
    modules: Const.QUILL_MODULES_PRIMARY,
    formats: Const.QUILL_FORMATS_PRIMARY,
    placeholder: '入力してください'
  })

  const { uploadFileDescription } = useTourUploadImage({
    buildingId: Number(buildingId),
    path: TourUploadImagePath.BUSINESS
  })

  // define mutate from useSWRConfig
  const { mutate } = useSWRConfig()

  /**
   * Helper to refresh building data in other components (e.g. ModalEditBuildingDetail)
   */
  const refreshBuildingData = () => {
    mutate(
      (key: unknown) => {
        const url =
          typeof key === 'string' ? key : (key as { url?: string })?.url
        return !!(
          url?.includes(`${apiPrefix}/buildings/by-type`) ||
          url === `${apiPrefix}/buildings/${buildingId}`
        )
      },
      undefined,
      { revalidate: true }
    )
  }

  /**
   * Saves the logo by uploading the selected file and updating the building's extra info.
   * If a logo is provided, it uploads the file using the `uploadFile` function.
   * Upon successful upload, it updates the building's logo and logo link using the `updateBusiness` mutation.
   * Displays a success message and closes the modal on successful update.
   *
   * @param values - The form values containing the logo and logoLink.
   */
  const handleSaveBusiness = async () => {
    setIsSavingData(true)
    // Upload description file
    const descriptionFile = await Resizer.ResizedQuillImages(
      quill?.root.innerHTML || ''
    )

    const blob = new Blob([descriptionFile], {
      type: 'text/plain'
    })

    if (blob.size / (1024 * 1024) > 50) {
      notification.error({
        key: 'memoTooLarge',
        message: t('common.validationError.fileTooLarge')
      })
    }

    if (blob) {
      const file = new File([blob], 'business_description.txt', {
        type: 'text/plain',
        lastModified: new Date().getTime()
      })

      const keyDescription = await uploadFileDescription(file)

      if (selectedFile) {
        // if change logo of business and description
        await uploadFile({
          onSuccess: (key) => {
            const dataUpdate = {
              businessImage: key,
              description: keyDescription
            }
            updateBusiness({
              method: 'patch',
              data: dataUpdate,
              successMessage: t('building.saved')
            })
              .then(() => {
                // closed modal when update logo success
                setIsSavingData(false)
                refreshBuildingData()
                handleCancel()
              })
              .catch(() => {
                setIsSavingData(false)
              })
          }
        })
      } else {
        // else change only description
        const dataUpdate = {
          description: keyDescription
        }
        updateBusiness({
          method: 'patch',
          data: dataUpdate,
          successMessage: t('building.saved')
        })
          .then(() => {
            // closed modal when update logo success
            setIsSavingData(false)
            refreshBuildingData()
            handleCancel()
          })
          .catch(() => {
            setIsSavingData(false)
          })
      }
    } else {
      setIsSavingData(false)
    }
  }

  /**
   * Deletes the logo by patching the building's extra info with an empty logo key.
   * Displays a success message and closes the modal on successful deletion.
   */
  const handleDeleteBusiness = () => {
    deleteLogo({
      method: 'patch',
      data: {
        businessImage: '',
        description: ''
      },
      successMessage: t('building.deleted')
    }).then(() => {
      // closed modal when delete logo success
      refreshBuildingData()
      handleCancel()
    })
  }

  /**
   * Handles the file input change event for updating the logo.
   * Validates and processes the selected file to update the form's logo field.
   * If the file is valid and of the correct type, sets the file as the selected logo
   * and updates the form field without errors.
   * If validation fails or the file type is incorrect, resets the logo field and displays
   * appropriate error messages.
   *
   * @param event - The change event triggered by the file input.
   */

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target
    const resetForm = () => {
      setSelectedFile(null)
      form.resetFields(['businessImage'])
    }

    if (files?.length) {
      form.setFields([{ name: 'businessImageWrapper', errors: [] }])

      Utils.validateFile(files[0], t)
        .then(() => {
          Utils.getFileMagicNumber(files[0], FileMagicNumber.IMAGE)
            .then(() => {
              setSelectedFile(files[0])
              form.setFieldValue('businessImage', files[0])
              form.setFields([{ name: 'businessImage', errors: [] }])
            })
            .catch(() => {
              form.setFields([
                {
                  name: 'businessImageWrapper',
                  errors: [t('translation.brokenFileMessage')]
                },
                { name: 'businessImage', value: '' }
              ])
              setSelectedFile(null)
            })
        })
        .catch((error: any) => {
          form.setFields([
            {
              name: 'businessImageWrapper',
              errors: [error.message]
            },
            { name: 'businessImage', value: '' }
          ])
          setSelectedFile(null)
          form.setFieldValue('businessImage', undefined)
        })
      return
    }

    resetForm()
  }
  const initialFormValue = useMemo(() => {
    return buildingInfo
      ? {
          businessImage: initialBusinessImage
        }
      : null
  }, [buildingInfo, initialBusinessImage])

  useEffect(() => {
    if (initialFormValue) {
      form.setFieldsValue(initialFormValue)
    }
  }, [form, initialFormValue])

  useEffect(() => {
    const loadInitialData = async () => {
      setIsLoadingInitData(true)
      if (buildingInfo && quill) {
        const { extraInfo } = buildingInfo
        try {
          if (extraInfo?.descriptionUrl) {
            const response = await fetch(extraInfo.descriptionUrl)
            const blob = await response.blob()
            const reader = new FileReader()
            reader.onload = () => {
              const text = reader.result as string
              quill.root.innerHTML = text
            }
            reader.readAsText(blob)
          } else {
            quill.root.innerHTML = ''
          }
          setInitialBusinessImage(extraInfo?.businessImageUrl)
          setIsLoadingInitData(false)
        } catch (error) {
          setIsLoadingInitData(false)
        }
      }
    }

    loadInitialData()
  }, [buildingInfo, quill])

  return {
    form,
    description,
    inputRef,
    businessLogoImage,
    isFormValid,
    selectedFile,
    buildingInfo,
    initialFormValue,
    isLogoLinkHasErrors,
    initialThumbnail,
    handleSaveBusiness,
    setSelectedFile,
    setIsLogoLinkHasErrors,
    handleChooseFile,
    handleFileChange,
    handleDeleteBusiness,
    isLoading,
    updating,
    deleting,
    t,
    quill,
    quillRef,
    isLoadingInitData,
    isSavingData
  }
}
